import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { FileText, LogIn, LogOut, MoveDown, Clock, Loader2 } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

function formatSeconds(s) {
  if (!s) return "—";
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function PageRow({ url, value, max, color = "#2f4f4f", sublabel }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="py-2 border-b border-border last:border-0">
      <div className="flex justify-between text-xs mb-1">
        <span className="truncate max-w-[260px] font-medium" title={url}>{url}</span>
        <span className="font-bold ml-2 shrink-0">{sublabel || value.toLocaleString()}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function AnalyticsBehaviour({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("pages");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/behaviour?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { id: "pages", label: "Top Pages", icon: FileText },
    { id: "entry", label: "Entry Pages", icon: LogIn },
    { id: "exit", label: "Exit Pages", icon: LogOut },
    { id: "scroll", label: "Scroll Depth", icon: MoveDown },
    { id: "time", label: "Time on Page", icon: Clock },
  ];

  const topPagesMax = Math.max(...(data?.top_pages || []).map((p) => p.views), 1);
  const entryMax = Math.max(...(data?.entry_pages || []).map((p) => p.count), 1);
  const exitMax = Math.max(...(data?.exit_pages || []).map((p) => p.count), 1);
  const scrollDist = data?.scroll_depth || {};
  const scrollMax = Math.max(...Object.values(scrollDist), 1);

  const scrollChartData = [
    { label: "25%", count: scrollDist["25"] || 0 },
    { label: "50%", count: scrollDist["50"] || 0 },
    { label: "75%", count: scrollDist["75"] || 0 },
    { label: "100%", count: scrollDist["100"] || 0 },
  ];

  return (
    <div className="space-y-5" data-testid="analytics-behaviour">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              subTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "pages" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-4">Most Visited Pages</h3>
          {data?.top_pages?.length > 0 ? (
            <div>
              {data.top_pages.slice(0, 15).map((page, i) => (
                <PageRow key={i} url={page.url} value={page.views} max={topPagesMax} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No page view data yet — tracking will start once visitors arrive
            </p>
          )}
        </div>
      )}

      {subTab === "entry" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-1">Entry Pages</h3>
          <p className="text-xs text-muted-foreground mb-4">First page visitors land on when arriving at your site</p>
          {data?.entry_pages?.length > 0 ? (
            <div>
              {data.entry_pages.map((page, i) => (
                <PageRow key={i} url={page.url} value={page.count} max={entryMax} color="#3b82f6" />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No entry page data yet</p>
          )}
        </div>
      )}

      {subTab === "exit" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-1">Exit Pages</h3>
          <p className="text-xs text-muted-foreground mb-4">Last page visited before a user left your site</p>
          {data?.exit_pages?.length > 0 ? (
            <div>
              {data.exit_pages.map((page, i) => (
                <PageRow key={i} url={page.url} value={page.count} max={exitMax} color="#e07b5d" />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No exit page data yet</p>
          )}
        </div>
      )}

      {subTab === "scroll" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-1">Scroll Depth Distribution</h3>
          <p className="text-xs text-muted-foreground mb-4">
            How far visitors scroll down pages — higher is better engagement
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scrollChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip
                content={({ active, payload, label }) =>
                  active && payload?.length ? (
                    <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
                      <p className="font-medium">Scrolled {label}</p>
                      <p className="text-primary">{payload[0]?.value} sessions</p>
                    </div>
                  ) : null
                }
              />
              <Bar dataKey="count" name="Sessions" fill="#2f4f4f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-4 gap-2 text-center">
            {scrollChartData.map((s, i) => (
              <div key={i} className="bg-muted/50 rounded p-2">
                <p className="text-sm font-bold">{s.count}</p>
                <p className="text-xs text-muted-foreground">reached {s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {subTab === "time" && (
        <div className="bg-white border border-border rounded-lg p-5">
          <h3 className="font-serif text-sm font-medium mb-1">Average Time on Page</h3>
          <p className="text-xs text-muted-foreground mb-4">Pages with the highest average engagement time</p>
          {data?.avg_time_on_page?.length > 0 ? (
            <div className="space-y-3">
              {data.avg_time_on_page.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <p className="text-xs truncate max-w-[280px]">{p.url}</p>
                  <span className="text-xs font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-2 shrink-0">
                    {formatSeconds(p.avg_seconds)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No time-on-page data yet</p>
          )}
        </div>
      )}
    </div>
  );
}
